// SettingsPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  SwipeableDrawer,
  Box,
  Divider,
  Button,
  Hidden,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageWrapper from '../../../components/page-wrapper';
import { ArrowForwardIos } from '@mui/icons-material';
import Password from '../../../components/forms/password';
import { useDispatch, useSelector } from 'react-redux';
import { themeReq } from '../../../store/slices/authSlice';
import { AuthUserSelector } from '../../../store/selector';
import Api from '../../../store/api';
import { showToast } from '../../../utils/toast';

interface Setting {
  label: string;
  subSettings?: boolean;
}

const maskMobileNumber = (value: string) => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length < 6) return value;
  return `${digits.slice(0, 2)}****${digits.slice(-4)}`;
};

const settingsList: Setting[] = [
  {
    label: 'Dark Theme',
    subSettings: false
  },
  {
    label: 'Change Password',
    subSettings: true,
  },
  {
    label: 'Blocked Profiles',
    subSettings: true,
  },
  {
    label: 'Delete Account',
    subSettings: false,
  },
];

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useSelector((state: any) => state.auth.theme);
  const authUser = useSelector(AuthUserSelector);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<Setting | null>(null);
  const [darkTheme, setDarkTheme] = useState(theme === 'dark');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOtpDialogOpen, setDeleteOtpDialogOpen] = useState(false);
  const [deleteOtp, setDeleteOtp] = useState('');
  const [deleteOtpMobile, setDeleteOtpMobile] = useState('');
  const [isSendingDeleteOtp, setIsSendingDeleteOtp] = useState(false);
  const [isVerifyingDeleteOtp, setIsVerifyingDeleteOtp] = useState(false);

  const handleSettingClick = (setting: Setting) => {
    if (!setting.subSettings) return;
    setSelectedSetting(setting);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setSelectedSetting(null);
    setDrawerOpen(false);
  };

  const handleThemeToggle = () => {
    const darkMode = theme !== "dark";
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    dispatch(themeReq(darkMode ? "dark" : "light"));
    setDarkTheme(!darkTheme);
  };

  const handleDeleteAccount = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      showToast('Unable to identify your account. Please sign in again.', 'error');
      return;
    }

    const mobile = authUser?.mobile || authUser?.username;
    if (!mobile) {
      showToast('Mobile number not found. Please sign in again.', 'error');
      return;
    }

    const confirmed = window.confirm(
      'Delete account permanently? This will remove your profile, related data, and gallery files from storage. This action cannot be undone.\n\nOTP verification is required.'
    );
    if (!confirmed) return;

    setIsSendingDeleteOtp(true);
    try {
      const otpRes = await Api.sendSignInOTP({ mobile });
      if (otpRes?.status === 200) {
        showToast(otpRes?.data?.message || 'OTP sent successfully.', 'success');
        setDeleteOtpMobile(mobile);
        setDeleteOtp('');
        setDeleteOtpDialogOpen(true);
        return;
      }
      showToast(otpRes?.data?.message || 'Failed to send OTP.', 'error');
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Failed to send OTP.', 'error');
    } finally {
      setIsSendingDeleteOtp(false);
    }
  };

  const handleVerifyOtpAndDelete = async () => {
    if (!deleteOtp || deleteOtp.length !== 6) {
      showToast('Enter a valid 6-digit OTP.', 'error');
      return;
    }

    setIsVerifyingDeleteOtp(true);
    try {
      const verifyRes = await Api.verifySignInOTP({ mobile: deleteOtpMobile, otp: deleteOtp });
      const statusCode = verifyRes?.data?.statusCode;
      const hasToken = !!verifyRes?.data?.accessToken || !!verifyRes?.data?.data?.accessToken;
      const otpVerified = statusCode === 1006 || hasToken;

      if (!otpVerified) {
        showToast(verifyRes?.data?.message || 'Invalid OTP.', 'error');
        return;
      }

      setIsDeleting(true);
      const deleteRes = await Api.deleteAccount();
      const deleteStatusCode = deleteRes?.data?.statusCode;
      const deleteSuccess = deleteStatusCode === 1010 || deleteRes?.data?.error === false;
      if (deleteSuccess) {
        showToast(deleteRes?.data?.message || 'Account deleted successfully.', 'success');
        localStorage.removeItem('accessToken');
        sessionStorage.clear();
        setDeleteOtpDialogOpen(false);
        setTimeout(() => {
          window.location.href = '/';
        }, 600);
        return;
      }

      showToast(deleteRes?.data?.message || 'Failed to delete account.', 'error');
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'OTP verification or delete failed.', 'error');
    } finally {
      setIsVerifyingDeleteOtp(false);
      setIsDeleting(false);
    }
  };

  const handleResendDeleteOtp = async () => {
    if (!deleteOtpMobile) return;
    setIsSendingDeleteOtp(true);
    try {
      const resendRes = await Api.sendSignInOTP({ mobile: deleteOtpMobile });
      if (resendRes?.status === 200) {
        showToast(resendRes?.data?.message || 'OTP resent successfully.', 'success');
        return;
      }
      showToast(resendRes?.data?.message || 'Failed to resend OTP.', 'error');
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Failed to resend OTP.', 'error');
    } finally {
      setIsSendingDeleteOtp(false);
    }
  };

  return (
    <>
      <Hidden mdUp>
        <Box sx={{ marginTop: "50px" }}></Box>
      </Hidden>
      <List>
        <Divider />
        {settingsList.map((setting, index) => (
          <React.Fragment key={index}>
            <ListItem onClick={() => handleSettingClick(setting)}>
              <ListItemText primary={setting.label} />
              {setting.label === 'Dark Theme' && <Switch checked={darkTheme} onChange={handleThemeToggle} />}
              {setting.label === 'Delete Account' && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={isDeleting || isSendingDeleteOtp || isVerifyingDeleteOtp}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAccount();
                  }}
                >
                  {isDeleting ? 'Deleting...' : isSendingDeleteOtp ? 'Sending OTP...' : 'Delete'}
                </Button>
              )}
              {setting.subSettings && <ArrowForwardIos />}
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        onOpen={() => { }}
        PaperProps={{ style: { width: '100%' } }}
      >
        <Box>
          <AppBar position="static">
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDrawerClose}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div">
                {selectedSetting?.label}
              </Typography>
            </Toolbar>
          </AppBar>
          {selectedSetting?.label === 'Change Password' && (
            <Password />
          )}
        </Box>
      </SwipeableDrawer>

      <Dialog open={deleteOtpDialogOpen} onClose={() => setDeleteOtpDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm OTP to Delete Account</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: '0.9rem' }}>
            Enter the OTP sent to {maskMobileNumber(deleteOtpMobile)}.
          </Typography>
          <TextField
            fullWidth
            label="OTP"
            value={deleteOtp}
            onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ maxLength: 6 }}
            placeholder="Enter 6-digit OTP"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResendDeleteOtp} disabled={isSendingDeleteOtp || isVerifyingDeleteOtp || isDeleting}>
            {isSendingDeleteOtp ? 'Resending...' : 'Resend OTP'}
          </Button>
          <Button onClick={() => setDeleteOtpDialogOpen(false)} disabled={isVerifyingDeleteOtp || isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleVerifyOtpAndDelete}
            disabled={isVerifyingDeleteOtp || isDeleting || deleteOtp.length !== 6}
          >
            {isVerifyingDeleteOtp || isDeleting ? 'Processing...' : 'Verify & Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsPage;
